/**
 * Search Niche Redux Slice
 * Manages state for niche search functionality
 */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ApiError } from '@http';
import { SEARCH_NICHE_CONFIG } from '@Constants/searchNiche.config';
import { STORAGE_KEY_ANALYSIS_ID, STORAGE_KEY_STAGE } from '@store/api/searchNicheApi';
import type { AnalysisSummaryResponse } from '@store/api/searchNicheApi';
import type { RootState } from '../types';

// Type definitions
export interface SearchNicheFilters {
  keyword: string;
  exactKeyword: boolean;
  marketplace: string;
  asins?: string[];
}

// Product tags structure
export interface ProductTags {
  keyword_1?: string;
  keyword_2?: string;
  keyword_3?: string;
}

// Product metrics structure
export interface ProductMetrics {
  approximate_30_day_units_sold?: number;
  approximate_30_day_revenue?: number;
  listing_quality_score?: number;
  rating?: number;
  reviews?: number;
  price?: number;
  amz_ref_fee?: number;
  amz_fba_fee?: number;
  all_amazon_fees?: number;
  all_amazon_fees_percentage?: number;
  est_COGS_percentage?: number;
  est_COGS?: number;
  sales?: number;
  sales_per_day?: number;
  listing_age?: number;
  weight_value?: number;
  length_value?: number;
  width_value?: number;
  height_value?: number;
  variations_count?: number;
  number_of_sellers?: number;
  [key: string]: number | undefined; // For dynamic rank_mean_category fields
}

// Product structure
export interface Product {
  product_name: string;
  asin: string;
  product_url: string;
  product_desc: string | null;
  price: number;
  price_currency: string | null;
  rating: number;
  reviews_summary: string | null;
  product_score: number;
  images_urls: string[];
  metrics: ProductMetrics;
  tags: ProductTags;
  rank_history?: Record<string, number>;
  sales_history?: Record<string, number>;
}

// A single listing's market depth data point (WE-444/WE-445).
export interface MarketDepthListing {
  asin: string | null;
  product_name: string | null;
  price: number | null;
  // Organic position in Amazon search results (cross-page, ads excluded);
  // null for products outside the organic SERP (e.g. force-included ASINs).
  position: number | null;
  revenue_value: number;
  units_sold: number;
}

// A single brand's share of the niche (WE-448/WE-449).
// Sorted by revenue_share_pct desc by the backend; "Other" (brands below the
// 3% threshold, collapsed) is always last and may appear even with
// brands_count === 0 (revenue attributed to brands outside the product sample).
export interface BrandDistributionEntry {
  brand: string;
  revenue_share_pct: number;
  revenue_value: number;
  listings_count: number;
  brands_count?: number;
}

// A single $5-bucket price segmentation entry (WE-442/WE-440)
export interface PriceSegmentationBucket {
  range_min?: number;
  range_max?: number;
  products_share?: number;
  revenue_share?: number;
  revenue_value?: number;
  avg_price?: number;
}

// Niche metrics structure
export interface NicheMetrics {
  // Price ranges (product distribution)
  price_range_0_10?: number;
  price_range_10_30?: number;
  price_range_31_60?: number;
  price_range_61_100?: number;
  price_range_101_150?: number;
  price_range_151_300?: number;
  price_range_300_plus?: number;

  // Revenue percentages by price range
  revenue_percent_0_10?: number;
  revenue_percent_10_30?: number;
  revenue_percent_31_60?: number;
  revenue_percent_61_100?: number;
  revenue_percent_101_150?: number;
  revenue_percent_151_300?: number;
  revenue_percent_300_plus?: number;

  // Revenue values by price range
  revenue_value_0_10?: number;
  revenue_value_10_30?: number;
  revenue_value_31_60?: number;
  revenue_value_61_100?: number;
  revenue_value_101_150?: number;
  revenue_value_151_300?: number;
  revenue_value_300_plus?: number;

  // Average prices by range
  avg_price_0_10?: number;
  avg_price_10_30?: number;
  avg_price_31_60?: number;
  avg_price_61_100?: number;
  avg_price_101_150?: number;
  avg_price_151_300?: number;
  avg_price_300_plus?: number;

  // Competition & Concentration
  percentage_products_with_sales_vs_all_ratio?: number;
  percentage_brands_with_successful_product_vs_all_brands_ratio?: number;
  avg_revenue_top20?: number;
  avg_revenue_top21_50?: number;
  avg_revenue_top51_100?: number;
  avg_revenue_top101_200?: number;
  percentage_products_makes_80_all_revenue?: number;
  percentage_revenue_top1_brand?: number;
  percentage_revenue_top3_products?: number;
  price_dispersion?: number | string;
  price_dispersion_class?: number | string;

  // Median revenue values by top segments (legacy, fallback only — kept for old snapshots without market_depth)
  median_revenue_value_within_top_10?: number;
  median_revenue_value_within_top_11_25?: number;
  median_revenue_value_within_top_26_50?: number;
  median_revenue_value_within_top_51_100?: number;
  median_revenue_value_within_top_101_200?: number;

  // Per-listing market depth (WE-444) — up to 200 listings, sorted by revenue_value desc on the frontend
  market_depth?: MarketDepthListing[];

  // Per-$5-bucket price segmentation (WE-442/WE-440) — replaces revenue_value_* keys in new snapshots
  price_segmentation?: PriceSegmentationBucket[];

  // Brand revenue/listings distribution within the niche (WE-448/WE-449)
  brand_distribution?: BrandDistributionEntry[];

  // Median reviews by top segments
  median_reviews_top20?: number;
  median_reviews_top21_50?: number;
  median_reviews_top51_100?: number;
  median_reviews_top101_200?: number;

  // Average rating by top segments
  avg_rating_top20?: number;
  avg_rating_top21_50?: number;
  avg_rating_top51_100?: number;
  avg_rating_top101_200?: number;
  avg_rating_all?: number;

  // Listing Quality & Reviews
  lqs_avg?: number;
  products_with_sales_and_less_100_reviews?: number;
  products_with_less_100_reviews?: number;
  new_winners_1y?: number;
  new_winners_6m?: number;

  // Demand & Trends
  products_from_api_count?: number;
  products_after_filter_count?: number;
  monthly_trend?: number;
  monthly_search_volume_exact?: number;
  quarterly_trend?: number;
  change_3_months_percent?: number;
  change_6_months_percent?: number;

  // Ads & Visibility
  recommended_promotions?: number;
  ppc_bid_exact?: number;
  ease_of_ranking_score?: number;
  relevancy_score?: number;
  organic_product_count?: number;
  sponsored_product_count?: number;
  ads_vs_sales_ratio?: number;
}

// Analysis tags (available at stage 5)
export interface AnalysisTags {
  generator_model_output?: string;
  reasoning?: string;
  final_report_text?: string;
  seed_keyword?: string;
  marketplace?: string;
  tokens_used?: number | string;
  [key: string]: string | number | undefined; // For dynamic asin fields
}

// Niche result structure
export interface SearchNicheResult {
  niche_id: string;
  keyword: string;
  price_min: number;
  price_max: number;
  products: Product[];
  metrics: NicheMetrics;
  tags: ProductTags | null;
  overall_score?: string; // Overall niche score/recommendation (e.g., 'Recommended', 'Not Recommended')
  ai_summary?: string; // AI-generated summary for this niche
}

export interface SearchNicheState {
  filters: SearchNicheFilters;
  niches: SearchNicheResult[];
  analysisTags: AnalysisTags | null;
  isAdvancedMode: boolean;
  loading: boolean;
  analysisId: number | null;
  error: string | null;
  status: 'idle' | 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  stage: number;
  isPolling: boolean;
  demoNiches: DemoNiche[];
  demoNichesLoading: boolean;
  summary: AnalysisSummaryResponse | null;
  summaryLoading: boolean;
  searchedAsin: string | null;
}

// Demo niche interface
export interface DemoNiche {
  id: number;
  keywords: string;
  data: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

// Load filters from localStorage
const loadFiltersFromStorage = (): SearchNicheFilters => {
  if (typeof window === 'undefined') {
    return { ...SEARCH_NICHE_CONFIG.defaultFilters };
  }

  try {
    const savedState = localStorage.getItem(SEARCH_NICHE_CONFIG.FORM_STATE_STORAGE_KEY);
    if (savedState) {
      const parsed = JSON.parse(savedState) as Partial<SearchNicheFilters>;
      return {
        keyword: parsed.keyword ?? SEARCH_NICHE_CONFIG.defaultFilters.keyword,
        exactKeyword: parsed.exactKeyword ?? SEARCH_NICHE_CONFIG.defaultFilters.exactKeyword,
        marketplace: parsed.marketplace ?? SEARCH_NICHE_CONFIG.defaultFilters.marketplace,
      };
    }
  } catch (error) {
    console.error('Error loading filters from localStorage:', error);
  }

  return { ...SEARCH_NICHE_CONFIG.defaultFilters };
};

// Initial state - always the same on server and client
export const initialState: SearchNicheState = {
  filters: loadFiltersFromStorage(),
  niches: [],
  analysisTags: null,
  isAdvancedMode: false,
  loading: false,
  analysisId: null,
  error: null,
  status: 'idle',
  progress: 0,
  stage: 0,
  isPolling: false,
  demoNiches: [],
  demoNichesLoading: false,
  summary: null,
  summaryLoading: false,
  searchedAsin: null,
};

// Async thunks
export const initiateNicheSearch = createAsyncThunk(
  'searchNiche/initiateSearch',
  async (filters: SearchNicheFilters, { rejectWithValue }) => {
    try {
      // Dynamic import to avoid circular dependency
      const { SearchNicheApiService } = await import('../api/searchNicheApi');
      const response = await SearchNicheApiService.initiateNicheAnalysis(filters);
      return {
        analysisId: response.analysis_id,
        filters
      };
    } catch (error) {
      
      if (error instanceof ApiError) {
        return rejectWithValue({
          message: error.message || 'Failed to initiate search',
          errorCode: error.errorCode
        });
      }
      
      return rejectWithValue({
        message: error instanceof Error ? error.message : 'Failed to initiate search',
        errorCode: undefined
      });
    }
  }
);

export const checkSearchStatus = createAsyncThunk(
  'searchNiche/checkStatus',
  async (analysisId: number, { rejectWithValue }) => {
    try {
      if (!analysisId) {
        return rejectWithValue('No active search analysis');
      }
      // Dynamic import to avoid circular dependency
      const { SearchNicheApiService } = await import('../api/searchNicheApi');
      return await SearchNicheApiService.getAnalysisState(analysisId);
    } catch (error) {
      console.error('Failed to check search status:', error);
      return rejectWithValue('Failed to check search status');
    }
  }
);

export const fetchDemoNiches = createAsyncThunk(
  'searchNiche/fetchDemoNiches',
  async (_, { rejectWithValue }) => {
    try {
      const { SearchNicheApiService } = await import('../api/searchNicheApi');
      return await SearchNicheApiService.getDemoNiches();
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to fetch demo niches';
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchLastSearch = createAsyncThunk(
  'searchNiche/fetchLastSearch',
  async (_, { rejectWithValue }) => {
    try {
      const { SearchNicheApiService } = await import('../api/searchNicheApi');
      const response = await SearchNicheApiService.getSearchHistory(1, 0);
      if (response && response.length > 0) {
        return response[0];
      }
      
      return rejectWithValue('No search history found');
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to fetch last search';
      return rejectWithValue(errorMessage);
    }
  }
);

export const loadDemoNiche = createAsyncThunk(
  'searchNiche/loadDemoNiche',
  async (
    // demoNicheId: number,
    nicheData: any,
     { dispatch, rejectWithValue }) => {
    try {
      // const { SearchNicheApiService } = await import('../api/searchNicheApi');
      
      // Simulate progressive loading with stages
      const stages = [
        { stage: 1, progress: 20, delay: 1000 },
        { stage: 2, progress: 40, delay: 2000 },
        { stage: 3, progress: 60, delay: 3000 },
        { stage: 4, progress: 80, delay: 4000 },
        { stage: 5, progress: 100, delay: 1000 },
      ];

      dispatch(updateAnalysisState({
        status: 'pending',
        progress: 0,
        stage: 0,
        analysisTags: { seed_keyword: nicheData.data.tags.seed_keyword }
      }));

      // Fetch demo niche data
      const demoNicheData = nicheData;
      // const demoNicheData = await SearchNicheApiService.getDemoNicheById(demoNicheId);

      // Simulate progress through stages
      for (const { stage, progress, delay } of stages) {
        await new Promise(resolve => setTimeout(resolve, delay));
        
        const updatePayload: {
          status: 'processing' | 'completed';
          progress: number;
          stage: number;
          niches?: SearchNicheResult[];
          analysisTags?: AnalysisTags;
        } = {
          status: stage === 5 ? 'completed' : 'processing',
          progress,
          stage,
        };

        if (stage >= 0) {
          updatePayload.analysisTags = { seed_keyword: demoNicheData.data.tags.seed_keyword };
        }
        // Add data at stage 3
        if (stage >= 3 && demoNicheData.data.niches) {
          updatePayload.niches = demoNicheData.data.niches;
        }

        // Add tags at stage 5
        if (stage === 5 && demoNicheData.data.tags) {
          updatePayload.analysisTags = demoNicheData.data.tags;
        }

        dispatch(updateAnalysisState(updatePayload));
      }

      return {
        niches: demoNicheData.data.niches,
        analysisTags: demoNicheData.data.tags,
        keyword: demoNicheData.keywords,
      };
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to load demo niche';
      return rejectWithValue(errorMessage);
    }
  }
);

const searchNicheSlice = createSlice({
  name: 'searchNiche',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<SearchNicheFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    toggleAdvancedMode: (state) => {
      state.isAdvancedMode = !state.isAdvancedMode;
    },
    resetSearch: (state) => {
      // Reset state but keep filters
      const filters = { ...state.filters };
      if (typeof window !== 'undefined') {
        localStorage.removeItem(STORAGE_KEY_STAGE);
        localStorage.removeItem(STORAGE_KEY_ANALYSIS_ID);
      }
      Object.assign(state, { ...initialState, filters });
    },
    setSummary: (state, action: PayloadAction<AnalysisSummaryResponse>) => {
      state.summary = action.payload;
      state.summaryLoading = false;
    },
    setSummaryLoading: (state, action: PayloadAction<boolean>) => {
      state.summaryLoading = action.payload;
    },
    setPolling: (state, action: PayloadAction<boolean>) => {
      state.isPolling = action.payload;
    },
    setAnalysisId: (state, action: PayloadAction<number | null>) => {
      state.analysisId = action.payload;
    },
    setSearchedAsin: (state, action: PayloadAction<string | null>) => {
      state.searchedAsin = action.payload;
    },
    updateAnalysisState: (state, action: PayloadAction<{
      status: 'pending' | 'processing' | 'completed' | 'failed';
      progress: number;
      stage: number;
      niches?: SearchNicheResult[];
      analysisTags?: AnalysisTags;
    }>) => {
      const { status, progress, stage, niches, analysisTags } = action.payload;

      if (state.status !== status) {
        state.status = status;
      }

      if (state.progress !== progress) {
        state.progress = progress;
      }

      // if (stage > state.stage) {
        state.stage = stage;


        if (niches && niches.length > 0 && !state.niches.length) {
          state.niches = niches;
        }

        // Обновляем analysisTags только при переходе на stage 5
        if (analysisTags) {
          state.analysisTags = analysisTags;
        }
      // }

      // Mark as completed when stage 5 is reached
      if (stage >= 5 || status === 'completed') {
        state.loading = false;
        state.status = 'completed';
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle initiateNicheSearch
      .addCase(initiateNicheSearch.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'pending';
        state.niches = [];
        state.analysisTags = null;
        state.progress = 0;
        state.stage = 0;
        state.summary = null;
        state.summaryLoading = false;
      })
      .addCase(initiateNicheSearch.fulfilled, (state, action) => {
        state.analysisId = action.payload.analysisId;
        state.filters = action.payload.filters;
        state.status = 'processing';
      })
      .addCase(initiateNicheSearch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to initiate search';
        state.status = 'failed';
      })

      // Handle checkSearchStatus
      .addCase(checkSearchStatus.fulfilled, (state, action) => {
        const { status, progress, niches, stage } = action.payload;

        state.status = status;
        state.progress = progress;
        state.stage = stage || 0;

        if (niches && niches.length > 0) {
          state.niches = niches;
        }

        if (status === 'completed') {
          state.loading = false;
        }
      })
      .addCase(checkSearchStatus.rejected, (state, action) => {
        console.error('Error checking search status:', action.payload);
      })

      // Handle fetchDemoNiches
      .addCase(fetchDemoNiches.pending, (state) => {
        state.demoNichesLoading = true;
      })
      .addCase(fetchDemoNiches.fulfilled, (state, action) => {
        state.demoNiches = action.payload;
        state.demoNichesLoading = false;
      })
      .addCase(fetchDemoNiches.rejected, (state, action) => {
        state.demoNichesLoading = false;
        console.error('Error fetching demo niches:', action.payload);
      })

      // Handle loadDemoNiche
      .addCase(loadDemoNiche.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.niches = [];
        state.analysisTags = null;
        state.analysisId = -1; // Use -1 to indicate demo mode
      })
      .addCase(loadDemoNiche.fulfilled, (state, action) => {
        state.loading = false;
        state.niches = action.payload.niches || [];
        state.analysisTags = action.payload.analysisTags || null;
        state.filters = {
          ...state.filters,
          keyword: action.payload.keyword,
        };
      })
      .addCase(loadDemoNiche.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to load demo niche';
        state.status = 'failed';
      });
  }
});

// Export actions
export const {
  setFilters,
  toggleAdvancedMode,
  resetSearch,
  setPolling,
  setAnalysisId,
  setSearchedAsin,
  updateAnalysisState,
  setSummary,
  setSummaryLoading,
} = searchNicheSlice.actions;

// Export selectors
export const selectSearchFilters = (state: RootState) => state.searchNiche.filters;
export const selectSearchNiches = (state: RootState) => state.searchNiche.niches;
export const selectAnalysisTags = (state: RootState) => state.searchNiche.analysisTags;
export const selectAnalysisMarketplace = (state: RootState) => state.searchNiche.analysisTags?.marketplace;
export const selectIsAdvancedMode = (state: RootState) => state.searchNiche.isAdvancedMode;
export const selectSearchStatus = (state: RootState) => state.searchNiche.status;
export const selectAnalysisId = (state: RootState) => state.searchNiche.analysisId;
export const selectIsSearchLoading = (state: RootState) => state.searchNiche.loading;
export const selectSearchProgress = (state: RootState) => state.searchNiche.progress;
export const selectSearchStage = (state: RootState) => state.searchNiche.stage;
export const selectIsPolling = (state: RootState) => state.searchNiche.isPolling;
export const selectDemoNiches = (state: RootState) => state.searchNiche.demoNiches;
export const selectDemoNichesLoading = (state: RootState) => state.searchNiche.demoNichesLoading;
export const selectNicheSummary = (state: RootState) => state.searchNiche.summary;
export const selectNicheSummaryLoading = (state: RootState) => state.searchNiche.summaryLoading;
export const selectSearchedAsin = (state: RootState) => state.searchNiche.searchedAsin;

// Backward compatibility selector (deprecated, use selectSearchNiches)
export const selectSearchResults = (state: RootState) => state.searchNiche.niches;

// Export reducer
export default searchNicheSlice.reducer;
